import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"

type SelectOpt = { id: number; name?: string; title?: string }
type VideoFormProps = { id: number | null; name: string; url: string; videoable_type: string; videoable_id: number | null }
type PreAttach = { type: string; id: number; name: string }
type PageProps = {
  video: VideoFormProps
  bands: SelectOpt[]
  people: SelectOpt[]
  recordings: Array<{ id: number; title: string }>
  venues: SelectOpt[]
  pre_attach?: PreAttach | null
  return_to?: string | null
  errors?: Record<string, string>
}

const ic = "w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-primary"

export default function VideoForm({ video, bands, people, recordings, venues, pre_attach, return_to }: {
  video: VideoFormProps
  bands: SelectOpt[]
  people: SelectOpt[]
  recordings: Array<{ id: number; title: string }>
  venues: SelectOpt[]
  pre_attach?: PreAttach | null
  return_to?: string | null
}) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = video.id === null
  const [form, setForm] = useState({
    name:           video.name ?? "",
    url:            video.url ?? "",
    videoable_type: video.videoable_type ?? "Band",
    videoable_id:   video.videoable_id !== null ? String(video.videoable_id) : "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const data = { video: form, ...(return_to ? { return_to } : {}) }
    if (isNew) router.post("/videos", data)
    else       router.patch(`/videos/${video.id}`, data)
  }

  const videoableOptions: Array<{ id: number; label: string }> = form.videoable_type === "Band"
    ? bands.map((b) => ({ id: b.id, label: b.name ?? "" }))
    : form.videoable_type === "Person"
    ? people.map((p) => ({ id: p.id, label: p.name ?? "" }))
    : form.videoable_type === "Recording"
    ? recordings.map((r) => ({ id: r.id, label: r.title }))
    : venues.map((v) => ({ id: v.id, label: v.name ?? "" }))

  const title = isNew ? "New video" : `Edit ${video.name}`
  const cancelHref = return_to ?? "/"

  return (
    <>
      <Head title={title}>
        <meta name="description" content={title} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={title} />
      </Head>
      <PublicShell>
        <div className="border-b border-hairline pb-6">
          <h1>{title}</h1>
          {isNew && pre_attach && (
            <p className="mt-1 text-sm text-ink-muted">
              Will be attached to <strong className="text-ink-body">{pre_attach.type}: {pre_attach.name}</strong>
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">
          <div>
            <label className="block text-sm text-ink-muted mb-1">Name</label>
            <input name="name" type="text" value={form.name} onChange={handleChange} className={ic} />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-sm text-ink-muted mb-1">YouTube or Vimeo URL</label>
            <input name="url" type="text" value={form.url} onChange={handleChange}
              placeholder="e.g. https://www.youtube.com/watch?v=… or https://vimeo.com/123456789"
              className={ic} />
            {errors.url && <p className="mt-1 text-xs text-red-600">{errors.url}</p>}
          </div>

          {pre_attach ? (
            <div>
              <p className="block text-sm text-ink-muted mb-1">Attached to</p>
              <p className="text-sm text-ink-body">{pre_attach.type}: {pre_attach.name}</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm text-ink-muted mb-1">Attached to type</label>
                <select name="videoable_type" value={form.videoable_type} onChange={handleChange} className={ic}>
                  <option value="Band">Band</option>
                  <option value="Person">Person</option>
                  <option value="Recording">Recording</option>
                  <option value="Venue">Venue</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-ink-muted mb-1">Attached to</label>
                <select name="videoable_id" value={form.videoable_id} onChange={handleChange} className={ic}>
                  <option value="">— select —</option>
                  {videoableOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            </>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create video" : "Update video"}</Button>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
